import React, { useState, useEffect } from 'react';

// Using the public CoinGecko endpoint. 
// Note: If you face CORS issues on your frontend, you may need to route this 
// through your Go backend proxy (/api/go).
const CG_TREASURY_URL = 'https://api.coingecko.com/api/v3/companies/public_treasury/bitcoin';

export const CorporateBTCTreasuryCard = ({ theme }) => {
  const [treasuryData, setTreasuryData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTreasury = async () => {
      try {
        const response = await fetch(CG_TREASURY_URL);
        const data = await response.json();
        setTreasuryData(data);
      } catch (err) {
        console.error('Error fetching corporate treasuries:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTreasury();
  }, []);

  if (loading) {
    return (
      <div style={{
        background: theme.cardBg, borderRadius: '16px', padding: '1.5rem',
        border: `1px solid ${theme.border}`, minHeight: '350px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textSecondary
      }}>
        Auditando tesourarias corporativas...
      </div>
    );
  }

  if (!treasuryData) return null;

  // Formatting helpers
  const formatUSD = (value) => {
    if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    return `$${value.toLocaleString()}`;
  };

  const formatBTC = (value) => {
    return new Intl.NumberFormat('en-US').format(Math.round(value));
  };

  // Grab the top 5 for the leaderboard
  const topCompanies = treasuryData.companies.slice(0, 5);
  // Find the max holdings to calculate relative visual bar widths
  const maxHoldings = Math.max(...topCompanies.map(c => c.total_holdings));

  return (
    <div style={{
      background: theme.cardBg,
      borderRadius: '16px',
      padding: '1.5rem',
      border: `1px solid ${theme.border}`,
      backdropFilter: 'blur(10px)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      flexDirection: 'column',
      gap: '1.5rem'
    }}>
      
      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{
            fontSize: '0.75rem', color: theme.textSecondary, textTransform: 'uppercase',
            fontWeight: 'bold', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem'
          }}>
            🏢 Adoção Institucional
            <span style={{ background: 'rgba(147, 51, 234, 0.1)', color: '#A855F7', padding: '2px 8px', borderRadius: '4px', fontSize: '0.65rem' }}>
              PUBLIC TREASURIES
            </span>
          </div>
          <h3 style={{ fontSize: '1.2rem', fontWeight: 'bold', color: theme.textPrimary, margin: 0 }}>
            Bitcoin em Tesouraria
          </h3>
        </div>
      </div>

      {/* MACRO STATS ROW */}
      <div style={{ 
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem',
        background: 'rgba(0,0,0,0.2)', padding: '1rem', borderRadius: '12px', border: `1px solid ${theme.borderHover}`
      }}>
        <div>
          <div style={{ fontSize: '0.75rem', color: theme.textSecondary }}>Total Acumulado</div>
          <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#F7931A', fontFamily: 'monospace' }}>
            {formatBTC(treasuryData.total_holdings)} <span style={{fontSize: '0.8rem'}}>BTC</span>
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.75rem', color: theme.textSecondary }}>Valor de Mercado (USD)</div>
          <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: theme.textPrimary, fontFamily: 'monospace' }}>
            {formatUSD(treasuryData.total_value_usd)}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.75rem', color: theme.textSecondary }}>% do Supply Global</div>
          <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#22c55e', fontFamily: 'monospace' }}>
            {treasuryData.market_cap_dominance.toFixed(2)}%
          </div>
        </div>
      </div>

      {/* LEADERBOARD */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ fontSize: '0.8rem', color: theme.textSecondary, fontWeight: 'bold', textTransform: 'uppercase', borderBottom: `1px solid ${theme.border}`, paddingBottom: '0.5rem' }}>
          Top 5 Corporate Holders
        </div>
        
        {topCompanies.map((company, index) => {
          const isGreen = company.total_current_value_usd > company.total_entry_value_usd;
          const roi = company.total_entry_value_usd > 0 
            ? ((company.total_current_value_usd - company.total_entry_value_usd) / company.total_entry_value_usd * 100).toFixed(1)
            : 'N/A'; // For companies where entry value is 0 (like miners)
            
          // Base log scale for visual bar so MSTR doesn't completely crush the others
          const fillPercentage = Math.max(5, (Math.log(company.total_holdings) / Math.log(maxHoldings)) * 100);

          return (
            <div key={company.symbol} style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ color: theme.textSecondary, fontSize: '0.9rem', fontWeight: 'bold', width: '15px' }}>
                    {index + 1}
                  </span>
                  <div>
                    <div style={{ color: theme.textPrimary, fontWeight: 'bold', fontSize: '0.95rem' }}>
                      {company.name} <span style={{ fontSize: '0.7rem', color: theme.textSecondary, marginLeft: '4px' }}>{company.symbol}</span>
                    </div>
                  </div>
                </div>
                
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: theme.textPrimary, fontWeight: 'bold', fontFamily: 'monospace' }}>
                    {formatBTC(company.total_holdings)} ₿
                  </div>
                  <div style={{ 
                    fontSize: '0.75rem', 
                    color: roi !== 'N/A' ? (isGreen ? '#22c55e' : '#ef4444') : theme.textSecondary,
                    fontWeight: 'bold'
                  }}>
                    {roi !== 'N/A' ? `${isGreen ? '+' : ''}${roi}% ROI` : 'Miner/Mined'}
                  </div>
                </div>
              </div>

              {/* Progress/Weight Bar */}
              <div style={{ width: '100%', height: '4px', background: 'rgba(255,255,255,0.05)', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ 
                  height: '100%', 
                  width: `${fillPercentage}%`, 
                  background: index === 0 ? '#F7931A' : 'rgba(247, 147, 26, 0.5)',
                  borderRadius: '2px',
                  transition: 'width 1s ease-in-out'
                }} />
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
};

export default CorporateBTCTreasuryCard;